import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, FileText, Sliders } from 'lucide-react';
import { ageAppropriateCurriculum, prettyCurriculumPref } from '../lib/reportUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirror of backend `_filter_by_curriculum` bucket logic so the warning page
// never labels a Cambridge school as "different curriculum family" when the
// parent chose `ib_or_cambridge`, etc.
const curriculumMatchesPref = (frameworkRaw, prefRaw) => {
  const sc = String(frameworkRaw || '').toLowerCase();
  const pref = String(prefRaw || '').trim().toLowerCase();
  if (!sc || !pref || pref === 'flexible') return true;

  let wantIb = false;
  let wantCam = false;
  let wantAm = false;
  let wantAu = false;
  if (pref.includes('ib_or_cambridge') || pref.includes('ib or cambridge')) {
    wantIb = true;
    wantCam = true;
  } else if (pref.includes('cambridge') || pref.includes('british')) {
    wantCam = true;
  } else if (pref.includes('american')) {
    wantAm = true;
  } else if (pref.includes('australian')) {
    wantAu = true;
  } else if (pref === 'ib' || pref.startsWith('ib ') || pref.includes('ib (') || pref.includes('international baccalaureate')) {
    wantIb = true;
  } else {
    return true; // Unknown / unmapped — don't flag as mismatch.
  }

  const isIb = sc.includes('ib') || sc.includes('international baccalaureate');
  const isCam = ['cambridge', 'igcse', 'a-level', 'a level', 'british'].some((t) => sc.includes(t));
  const isAm = sc.includes('american');
  const isAu = sc.includes('australian');
  return (wantIb && isIb) || (wantCam && isCam) || (wantAm && isAm) || (wantAu && isAu);
};

// Mirror of questionnaireData.js Q19 / Q20 / Q21 (compact pill labels only).
const CURRICULUM_OPTIONS = [
  { value: 'ib_or_cambridge', label: 'IB or Cambridge' },
  { value: 'ib', label: 'IB' },
  { value: 'cambridge', label: 'Cambridge / British' },
  { value: 'american', label: 'American' },
  { value: 'australian', label: 'Australian' },
  { value: 'flexible', label: 'Flexible' },
];

const BUDGET_OPTIONS = [
  { value: 'under_30k', label: 'Under RM30k' },
  { value: '30_50k', label: 'RM30–50k' },
  { value: '50_80k', label: 'RM50–80k' },
  { value: 'above_80k', label: 'Above RM80k' },
  { value: 'flexible', label: 'Flexible' },
];

const LOCATION_OPTIONS = [
  { value: 'mont_kiara', label: 'Mont Kiara / Dutamas' },
  { value: 'subang', label: 'Subang Jaya / PJ' },
  { value: 'ampang', label: 'Ampang / Cheras' },
  { value: 'damansara', label: 'Damansara' },
  { value: 'putrajaya', label: 'Putrajaya / Cyberjaya' },
  { value: 'kl_city', label: 'KL City Centre' },
  { value: 'flexible', label: 'Flexible' },
];

const Pills = ({ name, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid={`adjust-${name}-pills`}>
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          data-testid={`adjust-${name}-${opt.value}`}
          className={
            'px-3 py-1.5 rounded-full text-sm font-medium border transition ' +
            (active
              ? 'bg-[#E85D35] text-white border-[#E85D35]'
              : 'bg-white text-[#2C2823] border-[#E8DED3] hover:border-[#E85D35]')
          }
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

/**
 * Zero / thin-results safety net shown after questionnaire submission when
 * fewer than 3 schools pass all hard filters. Never lets a parent reach the
 * paywall on a blank report.
 */
const MatchWarningPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Inline adjust panel state
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustBudget, setAdjustBudget] = useState('');
  const [adjustCurriculum, setAdjustCurriculum] = useState('');
  const [adjustLocation, setAdjustLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/parent/report/${reportId}`);
        if (!res.ok) throw new Error('Report not found');
        const data = await res.json();
        const r = data.report || data;
        setReport(r);
        const s = r?.questionnaire_summary || r?.report_data?.questionnaire_summary || {};
        setAdjustBudget(s.budget || '');
        setAdjustCurriculum(s.curriculum_priority || '');
        setAdjustLocation(s.location_preference || '');
      } catch (e) {
        setError(e.message || 'Could not load report.');
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);

  const summary = report?.questionnaire_summary || report?.report_data?.questionnaire_summary || {};
  const tops = report?.report_data?.top_schools || [];
  const nears = report?.report_data?.near_misses || [];
  const combined = useMemo(() => [...tops, ...nears].slice(0, 3), [tops, nears]);

  const reasons = useMemo(() => {
    const r = [];
    if (summary.budget && summary.budget !== 'Flexible if the school is right' && summary.budget !== 'flexible') {
      r.push(`Budget: ${summary.budget}`);
    }
    if (summary.curriculum_priority && summary.curriculum_priority !== 'Flexible' && summary.curriculum_priority !== 'flexible') {
      r.push(`Curriculum: ${prettyCurriculumPref(summary.curriculum_priority)}`);
    }
    if (summary.location_preference && !String(summary.location_preference).toLowerCase().startsWith('flex')) {
      r.push(`Location: ${summary.location_preference}`);
    }
    if (summary.grade != null) r.push(`Year ${summary.grade}`);
    return r;
  }, [summary]);

  const oneLinerFor = (school) => {
    const grade = summary.grade || 1;
    const fees = school.annual_fees || {};
    const fee = fees[String(grade)];
    if (typeof school.fee_difference === 'number' && school.fee_difference > 0) {
      return `Fees about RM${Math.round(school.fee_difference / 1000)}k above your band`;
    }
    if (fee) {
      const band = String(summary.budget || '').toLowerCase();
      if (band.includes('under') && fee >= 30000) {
        return `Fees start at RM${Math.round(fee / 1000)}k — just above the Under-30k band`;
      }
      if (band.includes('50') && band.includes('80') && (fee < 50000 || fee > 80000)) {
        return `Year ${grade} fees RM${fee.toLocaleString()} sit outside your 50–80k band`;
      }
    }
    const cur = school.curriculum_framework || '';
    if (summary.curriculum_priority && cur && !curriculumMatchesPref(cur, summary.curriculum_priority)) {
      return `${ageAppropriateCurriculum(school, grade)} — different curriculum family`;
    }
    const locs = (school.campus_locations || [])[0];
    if (summary.location_preference && locs && !String(summary.location_preference).toLowerCase().startsWith('flex')) {
      return `Based in ${locs} — outside your ${summary.location_preference} preference`;
    }
    return 'Close to matching, but missing one of your key filters';
  };

  const handleSaveAdjustments = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${API_URL}/api/report/${reportId}/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          budget: adjustBudget,
          curriculum: adjustCurriculum,
          location: adjustLocation,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      if ((data.top_schools_count || 0) >= 3) {
        navigate(`/parent-report/${reportId}`);
        return;
      }
      // Still under threshold — refresh the warning page with updated filters.
      const refreshed = await fetch(`${API_URL}/api/parent/report/${reportId}`);
      const refreshedData = await refreshed.json();
      setReport(refreshedData.report || refreshedData);
      setShowAdjust(false);
    } catch (e) {
      setSaveError(e.message || 'Could not update filters.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F2]">
        <p className="text-[#6B6560]">Checking matches…</p>
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F2] p-6">
        <div className="max-w-md text-center">
          <p className="text-[#B4532A] font-semibold mb-3">We couldn't load your report.</p>
          <button
            onClick={() => navigate('/questionnaire')}
            className="px-5 py-3 rounded-lg bg-[#E85D35] text-white font-semibold"
            data-testid="warn-retry-btn"
          >
            Start a new questionnaire
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F2] py-10 px-4" data-testid="match-warning-page">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl bg-white border border-[#E8DED3] p-6 sm:p-8 shadow-[0_2px_20px_rgba(44,40,35,0.05)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">Match check</p>
              <h1 className="text-xl font-black text-[#2C2823]">We found fewer schools than usual</h1>
            </div>
          </div>

          <p className="text-[#2C2823] leading-relaxed mb-4">
            Based on your combination of filters, <strong>{tops.length}</strong> school{tops.length === 1 ? '' : 's'} passed every requirement.
            Some combinations (e.g. IB at the lower fee bands, or very specific locations) have limited supply in KL.
          </p>

          {reasons.length > 0 && !showAdjust && (
            <div className="rounded-xl bg-[#FFF5EB] border border-[#F0E5D7] p-4 mb-5" data-testid="warn-reasons">
              <p className="text-xs font-bold uppercase tracking-wider text-[#6B6560] mb-2">Your filters</p>
              <ul className="text-sm text-[#2C2823] space-y-1">
                {reasons.map((r) => (<li key={r}>• {r}</li>))}
              </ul>
            </div>
          )}

          {showAdjust && (
            <div
              className="rounded-xl bg-[#FFF5EB] border border-[#F0E5D7] p-5 mb-5 space-y-5"
              data-testid="adjust-panel"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B6560] mb-2">Budget</p>
                <Pills name="budget" options={BUDGET_OPTIONS} value={adjustBudget} onChange={setAdjustBudget} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B6560] mb-2">Curriculum</p>
                <Pills name="curriculum" options={CURRICULUM_OPTIONS} value={adjustCurriculum} onChange={setAdjustCurriculum} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B6560] mb-2">Location</p>
                <Pills name="location" options={LOCATION_OPTIONS} value={adjustLocation} onChange={setAdjustLocation} />
              </div>

              {saveError && (
                <p className="text-sm text-[#B4532A]" data-testid="adjust-save-error">{saveError}</p>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowAdjust(false)}
                  disabled={saving}
                  data-testid="adjust-cancel-btn"
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#E8DED3] text-[#2C2823] font-semibold hover:bg-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAdjustments}
                  disabled={saving}
                  data-testid="adjust-save-btn"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#E85D35] text-white font-semibold hover:bg-[#C84B31] disabled:opacity-60"
                >
                  {saving ? 'Re-checking…' : 'Re-run match'}
                </button>
              </div>
            </div>
          )}

          {!showAdjust && combined.length > 0 && (
            <div className="mb-6" data-testid="warn-near-misses">
              <p className="text-xs font-bold uppercase tracking-wider text-[#6B6560] mb-3">
                Closest schools we'd still surface
              </p>
              <ul className="space-y-3">
                {combined.map((s, i) => (
                  <li
                    key={s.id || i}
                    className="rounded-lg border border-[#E8DED3] bg-white p-4"
                    data-testid={`warn-near-miss-${i}`}
                  >
                    <p className="font-bold text-[#2C2823]">{s.full_name}</p>
                    <p className="text-sm text-[#6B6560] mt-1">{oneLinerFor(s)}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!showAdjust && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowAdjust(true)}
                data-testid="warn-adjust-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-[#E85D35] text-[#E85D35] font-semibold hover:bg-[#FFF5EB]"
              >
                <Sliders className="w-4 h-4" /> Adjust my preferences
              </button>
              <button
                onClick={() => navigate(`/parent-report/${reportId}`)}
                data-testid="warn-proceed-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#E85D35] text-white font-semibold hover:bg-[#C84B31]"
              >
                <FileText className="w-4 h-4" /> See my report anyway
              </button>
            </div>
          )}

          {!showAdjust && (
            <p className="text-xs text-[#9B8F88] mt-4 text-center">
              If you continue, we'll label these as "Closest matches — outside one of your filters" in your report.
            </p>
          )}

          {showAdjust && (
            <button
              onClick={() => navigate('/questionnaire')}
              data-testid="warn-restart-link"
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#6B6560] hover:text-[#2C2823]"
            >
              <ArrowLeft className="w-3 h-3" /> Or start the questionnaire from scratch
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchWarningPage;
